import type { ReactNode } from 'react'
import { clamp } from '@/lib/format'
import { cx } from '@/lib/cx'
import styles from './ProgressRing.module.css'

const RADIUS = 80
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/** Las cuatro marcas del sello se encienden al 25, 50, 75 y 100% de la sesión. */
const SEALS = [
  { key: 'norte', at: 0.25, x: 96.5, y: 13.5, width: 7, height: 13 },
  { key: 'este', at: 0.5, x: 173.5, y: 96.5, width: 13, height: 7 },
  { key: 'sur', at: 0.75, x: 96.5, y: 173.5, width: 7, height: 13 },
  { key: 'oeste', at: 1, x: 13.5, y: 96.5, width: 13, height: 7 },
] as const

interface ProgressRingProps {
  /** Progreso del tramo en curso, de 0 a 1. */
  progress: number
  /** Progreso de la sesión completa, de 0 a 1. */
  overall: number
  /** En descanso el trazo pierde el color: se ve de un vistazo. */
  resting?: boolean
  /** Versión pequeña, cuando debajo hay lista de tareas. */
  compact?: boolean
  /** `inset` deja el contenido dentro del aro interior: es lo que pide la figura. */
  center?: 'full' | 'inset'
  children?: ReactNode
}

export function ProgressRing({
  progress,
  overall,
  resting = false,
  compact = false,
  center = 'full',
  children,
}: ProgressRingProps) {
  const done = clamp(progress, 0, 1) * CIRCUMFERENCE

  return (
    <div className={cx(styles.box, compact && styles.compact)}>
      <svg className={cx(styles.ring)} viewBox="0 0 200 200" aria-hidden="true">
        <circle className={cx(styles.track)} cx="100" cy="100" r={RADIUS} />
        <circle
          className={cx(styles.progress, resting && styles.resting)}
          cx="100"
          cy="100"
          r={RADIUS}
          strokeDasharray={`${done.toFixed(1)} ${CIRCUMFERENCE.toFixed(1)}`}
        />
        <circle className={cx(styles.inner)} cx="100" cy="100" r="62" />
        {SEALS.map((seal) => (
          <rect
            key={seal.key}
            className={cx(styles.seal, overall >= seal.at - 0.001 && styles.sealOn)}
            x={seal.x}
            y={seal.y}
            width={seal.width}
            height={seal.height}
            rx="3.5"
          />
        ))}
      </svg>
      <div className={cx(styles.center, center === 'inset' && styles.centerInset)}>{children}</div>
    </div>
  )
}
